<template lang="pug">
.pack-record-products
  template(v-if="recordType === 'order'")
    .products-order-container
      .pack-record-products__item(
        v-for="product in dataForm.cart"
        :key="product.product"
        :style="productFlexStyle(product.product, cartFlexOrderMap)"
      )
        shProduct(
          :order="dataForm"
          :quantity="quantity"
          :product="product"
          @updateProduct="updateProduct"
          :subtract="false"
          :pick="dataForm.status === 'paid'"
          :allowPick="canPickProducts"
          :quantitySale="dataForm.quantitySale"
          :hideDescription="true"
        )

  template(v-else-if="recordType === 'productChange'")
    .products-order-container
      .pack-record-products__item(
        v-if="changeItem"
        :style="productFlexStyle(changeItem.product, changeFlexOrderMap)"
      )
        .pack-record-products__item-toolbar(v-if="productChangeSizeLabel")
          span.pack-record-products__size {{ productChangeSizeLabel }}
          .pack-record-products__item-toolbar-actions
            slot(name="product-change-actions")
        shProduct(
          :order="dataForm"
          :quantity="quantity"
          :product="changeItem"
          @updateProduct="updateProduct"
          :subtract="false"
          :pick="canPickProductChange"
          :allowPick="canPickProducts"
          :isProductChange="true"
          :hideDescription="true"
        )

  template(v-else-if="recordType === 'change'")
    .products-order-container
      .pack-record-products__item(
        v-for="product in dataForm.newProducts"
        :key="product.product"
        :style="productFlexStyle(product.product, newFlexOrderMap)"
      )
        shProduct(
          :change="dataForm"
          :quantity="totals.quantity"
          :product="product"
          @updateProduct="updateProduct"
          :subtract="false"
          :pick="dataForm.status === 'paid'"
          :isChange="true"
          :quantitySale="dataForm.quantitySale"
          :hideDescription="true"
        )

  UnpickTierModal(
    v-if="recordType === 'order' || recordType === 'productChange'"
    :show="unpickTierModal.show"
    :pickedTier="unpickTierModal.pickedTier"
    :item="unpickTierModal.item"
    :busy="unpickTierModal.busy"
    @close="closeUnpickTierModal"
    @select="onUnpickTierSelect"
  )
</template>

<script>
import mForm from '@/mixins/form'
import shProduct from '@/components/ecommerce/Product'
import UnpickTierModal from '@/components/pages/order/UnpickTierModal.vue'
import { isBlockedByFraud } from '@/helpers/fraudReview'

export default {
  name: 'PackRecordProducts',

  mixins: [mForm],

  components: {
    shProduct,
    UnpickTierModal
  },

  props: {
    dataOriginal: {
      type: Object,
      required: true
    },
    recordType: {
      type: String,
      required: true,
      validator: value => value === 'order' || value === 'change' || value === 'productChange'
    }
  },

  provide () {
    return {
      chooseUnpickReturnTier: (this.recordType === 'order' || this.recordType === 'productChange')
        ? this.chooseUnpickReturnTier
        : null
    }
  },

  data () {
    return {
      dataForm: {},
      unpickTierModal: {
        show: false,
        pickedTier: '',
        item: null,
        busy: false,
        resolve: null,
        reject: null
      }
    }
  },

  computed: {
    quantity () {
      return this.dataForm.quantity || 0
    },

    totals () {
      if (!this.dataForm.newProducts) return { quantity: 0 }
      return this.getTotals(this.dataForm.newProducts)
    },

    cartFlexOrderMap () {
      return this.buildFlexOrderMap(this.dataForm.cart)
    },

    newFlexOrderMap () {
      return this.buildFlexOrderMap(this.dataForm.newProducts)
    },

    changeItem () {
      if (!this.dataForm.productChange || !this.dataForm.productChange.item) return null
      return this.dataForm.productChange.item
    },

    changeFlexOrderMap () {
      if (!this.changeItem) return {}
      return { [this.changeItem.product]: 0 }
    },

    productChangeSizeLabel () {
      const change = this.dataForm.productChange
      if (!change) return null
      return `${change.from} → ${change.to}`
    },

    canPickProductChange () {
      return ['paid', 'packed', 'sent'].includes(this.dataForm.status)
    },

    canPickProducts () {
      if (this.recordType === 'productChange') {
        return this.canPickProductChange && !isBlockedByFraud(this.dataForm)
      }

      return this.dataForm.status === 'paid' && !isBlockedByFraud(this.dataForm)
    }
  },

  created () {
    this.dataForm = this.copyObject(this.dataOriginal)
  },

  watch: {
    dataOriginal: {
      handler () {
        this.dataForm = this.copyObject(this.dataOriginal)
      },
      deep: true
    }
  },

  methods: {
    buildFlexOrderMap (products) {
      if (!products || !products.length) return {}

      const sorted = products
        .map(product => product.product)
        .slice()
        .sort((a, b) => String(a).localeCompare(String(b), 'es', { sensitivity: 'base' }))

      const map = {}
      sorted.forEach((code, index) => {
        map[code] = index
      })

      return map
    },

    productFlexStyle (productCode, orderMap) {
      const order = orderMap[productCode]
      return order != null ? { order } : {}
    },

    updateProduct () {},

    chooseUnpickReturnTier ({ product, size, pickedTier, brand, folder, subfolder, images }) {
      return new Promise((resolve, reject) => {
        this.unpickTierModal = {
          show: true,
          pickedTier,
          item: {
            product,
            size,
            quantity: 1,
            brand: brand || this.dataOriginal.brand,
            folder,
            subfolder,
            image: images && images.length ? images[0] : null
          },
          busy: false,
          resolve,
          reject
        }
      })
    },

    closeUnpickTierModal () {
      if (this.unpickTierModal.busy) return
      if (this.unpickTierModal.reject) this.unpickTierModal.reject(new Error('cancelled'))
      this.unpickTierModal.show = false
      this.unpickTierModal.resolve = null
      this.unpickTierModal.reject = null
    },

    onUnpickTierSelect (returnTier) {
      if (this.unpickTierModal.resolve) this.unpickTierModal.resolve(returnTier)
      this.unpickTierModal.show = false
      this.unpickTierModal.resolve = null
      this.unpickTierModal.reject = null
    }
  }
}
</script>

<style lang="stylus" scoped>
.pack-record-products
  margin-top 0
  width 100%

  .products-order-container
    margin-top 0

.pack-record-products__item
  width 100%
  box-sizing border-box

.pack-record-products__item-toolbar
  display flex
  align-items center
  justify-content space-between
  gap .5rem
  width 100%
  margin-bottom .5rem

.pack-record-products__size
  flex 1
  min-width 0
  font-size 1.1rem
  font-weight 700
  letter-spacing .04em
  text-transform uppercase
  text-align left
  color var(--color-font-principal, #363636)

.pack-record-products__item-toolbar-actions
  flex-shrink 0
</style>



// WEBPACK FOOTER //
// src/components/pages/order/pack/PackRecordProducts.vue